import { isStringArray } from '@/api/queries/utils';
import { FilterType } from '@/models/filter/FilterType';
import { isRecord } from '@/utils/filter/isRecord';

const FILTER_TYPE_VALUES: ReadonlySet<string> = new Set(Object.values(FilterType));

/**
 * Maps legacy/renamed global-filter keys to their current FilterType value.
 *
 * The FilterType string values are a persisted wire contract: they are stored
 * verbatim inside saved and shared filter payloads (S3) and the local
 * `filter-draft`. Renaming a FilterType value therefore breaks every filter a
 * user saved under the old name — the value is silently dropped on read.
 *
 * When you rename or fix a FilterType value, add an entry here mapping the
 * OLD stored key to the NEW FilterType value so historical payloads migrate
 * on read instead of losing data. Keys not covered here and not present in the
 * current FilterType enum are dropped (see migrateFilterKeys).
 *
 * The contract test in `filterKeyMigrations.test.ts` fails if a FilterType
 * value changes without a corresponding migration entry.
 */
export const FILTER_KEY_MIGRATIONS: Readonly<Record<string, FilterType>> = {
  // Typo fixed: 'secondarySepcialty' -> 'secondarySpecialty' (!3132)
  secondarySepcialty: FilterType.SECONDARY_SPECIALTY,
};

/**
 * Normalize the keys of a persisted globalFilters object against the current
 * FilterType enum:
 *  - keys already valid are kept as-is,
 *  - keys listed in FILTER_KEY_MIGRATIONS are renamed to the current value,
 *  - all other (unknown/removed) keys are dropped.
 *
 * A migrated key never overwrites a value already present under the canonical
 * key: if a payload somehow contains both the old and new key, the canonical
 * value wins and the legacy value is discarded.
 */
export function migrateFilterKeys(globalFilters: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(globalFilters)) {
    if (FILTER_TYPE_VALUES.has(key)) {
      result[key] = value;
    }
  }

  for (const [oldKey, newKey] of Object.entries(FILTER_KEY_MIGRATIONS)) {
    if (oldKey in globalFilters && !(newKey in result)) {
      result[newKey] = globalFilters[oldKey];
    }
  }

  return result;
}

/**
 * Normalize a persisted `optionPaths` record against the current FilterType
 * enum, mirroring {@link migrateFilterKeys}: valid keys are kept, legacy keys
 * are renamed, and unknown/removed keys are dropped — as is any entry whose
 * value is not a string array.
 *
 * Unlike globalFilters, an unusable entry here cannot simply be left in place:
 * `isOptionPathsByFilter` rejects the whole `optionPaths` object over a single
 * unmappable key, which fails `isSharedViewPayload` and surfaces to the
 * recipient as "Cannot load view" instead of loading the view without its
 * option paths. Dropping the entry keeps the registry free of paths this build
 * cannot map to a filter while leaving the view loadable, matching how
 * `fetchFilterPayload` treats the same input.
 */
export function migrateOptionPathKeys(optionPaths: Record<string, unknown>): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  for (const [key, value] of Object.entries(optionPaths)) {
    if (FILTER_TYPE_VALUES.has(key) && isStringArray(value)) {
      result[key] = value;
    }
  }

  for (const [oldKey, newKey] of Object.entries(FILTER_KEY_MIGRATIONS)) {
    const legacyValue = optionPaths[oldKey];
    if (!(newKey in result) && isStringArray(legacyValue)) {
      result[newKey] = legacyValue;
    }
  }

  return result;
}

/**
 * Apply key migration to an unwrapped S3 payload, handling both the
 * `{ globalFilters, ... }` envelope and the legacy flat FilterState shape.
 * Non-record values pass through unchanged.
 */
export function migrateSharedViewKeys(data: unknown): unknown {
  if (!isRecord(data)) {
    return data;
  }

  if ('globalFilters' in data && isRecord(data.globalFilters)) {
    const migrated: Record<string, unknown> = { ...data, globalFilters: migrateFilterKeys(data.globalFilters) };

    // A non-record `optionPaths` carries no recoverable paths, but leaving it
    // in place would still fail the payload guard, so it normalizes to empty.
    if ('optionPaths' in data && data.optionPaths !== undefined) {
      migrated.optionPaths = isRecord(data.optionPaths) ? migrateOptionPathKeys(data.optionPaths) : {};
    }

    return migrated;
  }

  return migrateFilterKeys(data);
}
